import { geojson } from "@lib/geojson";
import { Feature, Map as OlMap, Overlay } from "ol";
import { FeatureLike } from "ol/Feature";

export class TooltipManager {
    private mapElement: OlMap;
    private activeTooltips: Map<Feature|FeatureLike, { overlay: Overlay, category: string, type: string }>;

    constructor(mapElement: OlMap) {
        this.mapElement = mapElement;
        this.activeTooltips = new Map(); // Track tooltips by feature
    }

    public showTooltip(
        features: {
            feature: Feature | FeatureLike,
            content: string,
            coordinate: geojson.Coord,
            category: 'individual'|'group',
            type: string
        }[]) {
        features.forEach(({ feature, content, coordinate, category, type }) => {
            const existingTooltip = this.activeTooltips.get(feature);
            // Check if a tooltip already exists for this feature
            if (existingTooltip) {
                this.setOverlayContent(existingTooltip.overlay, content);
                existingTooltip.overlay.setPosition(coordinate);
                this.activeTooltips.set(feature, { overlay: existingTooltip.overlay, category, type });
                return;
            }

            // Create the tooltip element
            const tooltipElement = this.createTooltipElement(content);

            // Create an overlay for the tooltip
            const tooltipOverlay = new Overlay({
                element: tooltipElement,
                positioning: 'center-center',
                offset: [0, -10],
            });

            tooltipOverlay.setPosition(coordinate);

            // Add the tooltip to the map
            this.mapElement.addOverlay(tooltipOverlay);

            // Track this tooltip with its category
            this.activeTooltips.set(feature, { overlay: tooltipOverlay, category,type });
        });
    }

    public updateTooltip(feature: Feature | FeatureLike, content: string, coordinate: geojson.Coord) {
        const tooltip = this.activeTooltips.get(feature);
        if (!tooltip) {
            return;
        }

        this.setOverlayContent(tooltip.overlay, content);
        tooltip.overlay.setPosition(coordinate);
    }

    public hideTooltip(feature: Feature|FeatureLike) {
        const tooltip = this.activeTooltips.get(feature);
        if (tooltip) {
            this.mapElement.removeOverlay(tooltip.overlay);
            const element = tooltip.overlay.getElement();
            if (element && element.parentNode) {
                element.parentNode.removeChild(element); // Remove the DOM element
            }
            this.activeTooltips.delete(feature); // Remove from the map
        }
    }

    public hideAllTooltips(type: string) {
        Array.from(this.activeTooltips.entries()).forEach(([feature, tooltip]) => {
            if (tooltip.type === type) {
                this.hideTooltip(feature);
            }
        });
    }

    private createTooltipElement(content: string) {
        const tooltipElement = document.createElement('div');
        tooltipElement.className = 'ol-tooltip';
        tooltipElement.style.position = 'absolute';
        tooltipElement.style.backgroundColor = 'rgba(255, 255, 255, 0.95)';
        tooltipElement.style.color = 'black';
        tooltipElement.style.padding = '0.25em';
        tooltipElement.style.borderRadius = '0.15em';
        tooltipElement.style.whiteSpace = 'nowrap';
        tooltipElement.style.fontSize = '0.75em';
        tooltipElement.style.pointerEvents = 'none';
        tooltipElement.style.zIndex = '100000';
        tooltipElement.innerHTML = content;

        return tooltipElement;
    }

    private setOverlayContent(overlay: Overlay, content: string) {
        const element = overlay.getElement();
        if (element instanceof HTMLElement) {
            element.innerHTML = content;
        }
    }
}
